import type { ParcelDto } from "@babackapp/private-api-client/generated/schema";
import { cn } from "@babackapp/ui/lib/utils";
import { Column, Row, Text } from "@react-email/components";
import { format } from "date-fns";
import useThemeContextualColors, { sectionToParent } from "../../hooks/use-theme-contextual-colors";
import { tLayout } from "../../translations";
import type { EstimatedDeliveryDateBlockProps } from "./types";
import { findArrivedEvent, findDeliveredEvent, formatDeliveryDateWindow, getDateFnsLocale } from "./utils";


export function EstimatedDeliveryDateEmailRenderer({
  block,
  parcel,
  theme,
  section,
  locale,
  defaultLocale,
}: EstimatedDeliveryDateBlockProps) {
  const alignment = block?.alignment || "center";
  const wrap = block?.properties?.wrap ?? false;
  const t = tLayout(locale, defaultLocale);
  const dateWindow = parcel?.estimatedDeliveryDateWindow;
  const colors = useThemeContextualColors(theme, sectionToParent(section));
  if (!dateWindow) return null;

  const { exactDeliveryDay, minimumDeliveryDate, maximumDeliveryDate } =
    dateWindow;

  const customerLocale = locale || (parcel as ParcelDto)?.customerLocale || defaultLocale;
  const dateFnsLocale = getDateFnsLocale(customerLocale);

  // Delivered to final destination
  const deliveredEvent = findDeliveredEvent(parcel as ParcelDto);

  const alignmentClasses = {
    "text-left": alignment === "left",
    "text-center": alignment === "center",
    "text-right": alignment === "right",
  };

  if (deliveredEvent) {
    const deliveredDate = format(
      deliveredEvent?.createdAt || new Date(),
      "d MMMM yyyy",
      { locale: dateFnsLocale },
    );

    return (
      <Row>
        <Column
          className={cn("rounded-lg", `bg-[${colors.bg}]`, alignmentClasses)}
        >
          {wrap ? (
            <>
              <Text
                className={cn("w-full font-medium m-0", alignmentClasses)}
                style={{ margin: 0, color: colors.text }}
              >
                {t("tracking.shared.deliveredOn")}
              </Text>
              <Text
                className={cn(
                  "w-full text-md font-semibold m-0",
                  alignmentClasses,
                )}
                style={{ margin: 0, color: colors.text }}
              >
                {deliveredDate}
              </Text>
            </>
          ) : (
            <Text
              className={cn("w-full font-medium m-0", alignmentClasses)}
              style={{ margin: 0, color: colors.text }}
            >
              {t("tracking.shared.deliveredOn")} {deliveredDate}
            </Text>
          )}
        </Column>
      </Row>
    );
  }

  // Arrived to destination (client or neighbour or pickup point)
  const arrivedEvent = findArrivedEvent(parcel as ParcelDto);

  if (arrivedEvent) {
    return null;
  }

  let displayDate = "";

  if (exactDeliveryDay) {
    displayDate = format(exactDeliveryDay, "EEEE d MMMM yyyy", {
      locale: dateFnsLocale,
    });
  } else if (minimumDeliveryDate && maximumDeliveryDate) {
    displayDate = formatDeliveryDateWindow(
      minimumDeliveryDate,
      maximumDeliveryDate,
      customerLocale,
      {
        from: t("tracking.shared.from"),
        to: t("tracking.shared.to"),
      },
    );
  } else if (minimumDeliveryDate) {
    displayDate = format(minimumDeliveryDate, "EEEE d MMMM yyyy", {
      locale: dateFnsLocale,
    });
  }

  if (!displayDate?.length) {
    return null;
  }

  return (
    <Row>
      <Column
        className={cn("rounded-lg", `bg-[${colors.bg}]`, alignmentClasses)}
      >
        {wrap ? (
          <>
            <Text
              className={cn("w-full font-medium m-0", alignmentClasses)}
              style={{ margin: 0, color: colors.text }}
            >
              {t("tracking.shared.estimatedDeliveryDate")}
            </Text>
            <Text
              className={cn(
                "w-full text-md font-semibold m-0",
                alignmentClasses,
              )}
              style={{ margin: 0, color: colors.text }}
            >
              {displayDate}
            </Text>
          </>
        ) : (
          <Text
            className={cn("w-full font-medium m-0", alignmentClasses)}
            style={{ margin: 0, color: colors.text }}
          >
            {t("tracking.shared.estimatedDeliveryDate")}{" "}
            <span className="m-0 text-md font-semibold">
              {displayDate}
            </span>
          </Text>
        )}
      </Column>
    </Row>
  );
}
